import { assertIsDefined } from '@sgme/fp';

import type { WebapiEndpointBuilder } from '@/api/webapi.ts';
import { getCurrenciesFromCurrencyPair } from '@/App/tiles/FlexiForward/utils.ts';
import type {
  FlexiForwardRfsRequest,
  FlexiForwardRfsRequestFxoHedge,
} from '@/store/flexi-forward/api/requestFlexiForwardRfs.model.ts';
import type { FxFlexiForward } from '@/store/flexi-forward/state.ts';

export function updateFlexiForwardRfs(builder: WebapiEndpointBuilder) {
  return builder.mutation<void, FlexiForwardRfsRequest>({
    query: (params) => {
      return {
        url: '/fxoAmericanForward/rfs/update',
        method: 'POST',
        body: params.body,
        headers: {
          'X-Correlation-Id': params.rfsId,
        },
      };
    },
  });
}

export function createFlexiForwardUpdateRfsParams(tile: FxFlexiForward, tileId: string, rfsId: string) {
  const { currencyPair, hedgeType, rfsVersion, premiumWay, rate, solvingType, upfront, upfrontCurrency } =
    tile.properties;
  const { currency2 } = getCurrenciesFromCurrencyPair(currencyPair);

  const hedges = hedgeType !== 'Live' ? [getHedge(tile.properties)] : [];

  const isStrikeSolving = solvingType !== 'solveUpfront';
  const isSolving = isStrikeSolving ? { isSolving: true } : {};

  const premiumKey = premiumWay === 'Bid' ? 'premiumBidValue' : 'premiumAskValue';
  const premiumCurrency = upfrontCurrency === currency2 ? 2 : 1;
  const premiumValues = isStrikeSolving ? { [premiumKey]: upfront, premiumCurrency } : {};

  const strikePrice = isStrikeSolving ? null : (rate ?? 0);

  return {
    tileId,
    rfsId,
    body: {
      rfsId,
      version: (rfsVersion ?? 0) + 1,

      fxOptionProduct: {
        hedges,
        ...isSolving,

        legs: [
          {
            id: tileId,
            ...premiumValues,
            strikePrice,
          },
        ],
      },
    },
  };
}

function getHedge({
  spotHedge: maturityDate,
  deltaHedgeAmount,
  deltaHedgeCurrencyIndex,
}: FxFlexiForward['properties']): FlexiForwardRfsRequestFxoHedge {
  assertIsDefined(maturityDate, 'hedgePaymentDate is mandatory for pricing when hedgeType is Spot or Forward');

  return {
    hedgeType: 'Spot',
    maturityDate: new Date(maturityDate),
    notionalAmount: deltaHedgeAmount?.toString() ?? '',
    notionalCurrency: deltaHedgeCurrencyIndex ?? 1,
    price: '',
  };
}
